import { useLayoutEffect, useMemo, useRef, useState } from "react";
import { Box, Chip, Typography, Tooltip, IconButton, Button, Link, TextField } from "@mui/material";
import HistoryIcon from "@mui/icons-material/History";
import EditIcon from "@mui/icons-material/Edit";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import dayjs from "dayjs";
import { colors } from "../../../../theme";
import { SidePanelSection, SidePanelStatGrid, SidePanelKV } from "../../../../components/SidePanel";
import {
  tagColor as staticTagColor, vulnSeverityTier, severityStyleForTier,
  worstVulnerability, allVulnerabilitiesForAsset,
  osIcon, OS_LEVELS, confidenceStyle, riskScoreStyle, parseRiskFactors, mergeRiskFactors,
  hostnameAlternatives,
} from "./assetUtils";
import { resolveClassification, humanizeTaxonomy } from "./assetClassification";
import VulnerabilityList, { ExposureChip } from "./VulnerabilityList";
import ProtocolIdentitySection from "./ProtocolIdentitySection";
import TruncatedText from "./TruncatedText";

// Handles { $date: "..." } and ISO strings
const parseDate = (v) => {
  if (!v) return null;
  const raw = v?.$date ?? v;
  const d = dayjs(raw);
  return d.isValid() ? d : null;
};

const fmt = (v) => {
  const d = parseDate(v);
  return d ? d.format("YYYY-MM-DD HH:mm:ss") : null;
};

// Small "where did this value come from" tag — shown next to every field the
// document can attribute to a specific source (os.*_source, vendor_source,
// device.source, ...). Sources are short verbatim strings (`smb1`, `ntlm_exact`,
// `dhcp_param_list`); shown as-is, since the full translation table lives in the
// probe repo and paraphrasing here would go stale.
const SourceChip = ({ source }) =>
  source ? (
    <Chip
      label={source}
      size="small"
      sx={{ bgcolor: "#ffffff0d", color: colors.grey[400], fontSize: 9.5, height: 16, fontWeight: 600 }}
    />
  ) : null;

// A derived value's confidence, colour-coded by how strong the evidence behind it
// is. Never hidden: 90 and 25 are the difference between a fact and a conjecture.
const ConfidenceChip = ({ confidence }) => {
  const style = confidenceStyle(confidence);
  if (!style) return null;
  return (
    <Tooltip title={`Confidence ${confidence}/100 — ${style.label}`}>
      <Chip
        label={confidence}
        size="small"
        sx={{ bgcolor: `${style.color}22`, color: style.color, fontSize: 9.5, height: 16, fontWeight: 700, minWidth: 26 }}
      />
    </Tooltip>
  );
};

const GroupLabel = ({ children }) => (
  <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: colors.grey[500], textTransform: "uppercase", letterSpacing: 0.5, mt: 1, mb: 0.3, "&:first-of-type": { mt: 0.5 } }}>
    {children}
  </Typography>
);

const Empty = ({ children }) => (
  <Typography sx={{ color: colors.grey[600], fontSize: 12.5, fontStyle: "italic" }}>{children}</Typography>
);

// A block below is passed to this card as `<Component .../>` — a React element,
// which is *always* a truthy object regardless of what that component decides
// to render. Checking the element's truthiness (as the old `blocks.filter((b)
// => b.node)` did) therefore can never catch a component that internally
// returns null, and the card border/background around it would render anyway.
// Detecting real emptiness needs the actual DOM output, so this card renders
// its child, then (after commit, before paint — no visible flash) checks
// whether anything actually landed in it and hides itself via CSS if not.
// Keeps the child mounted either way, so components with their own state
// (`useState` for "show all", etc.) don't lose it as sibling data changes.
const AutoHideCard = ({ children, sx }) => {
  const ref = useRef(null);
  const [hasContent, setHasContent] = useState(true);

  // Deliberately no dependency array: this has to re-check after every commit
  // (a prop change re-populating `row` without remounting, an async list
  // arriving late, a child's own "show all" toggle) — not just once on mount.
  // Safe from an update loop because setHasContent is a no-op render when the
  // computed value doesn't change.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useLayoutEffect(() => {
    setHasContent((ref.current?.childNodes.length ?? 0) > 0);
  });

  return (
    <Box
      ref={ref}
      sx={{
        display: hasContent ? undefined : "none",
        backgroundColor: "rgba(69,144,149,0.045)",
        border: "1px solid rgba(69,144,149,0.14)",
        borderRadius: 1.5,
        p: { xs: 1.5, md: 2 },
        "& .MuiDivider-root:last-child": { display: "none" },
        ...sx,
      }}
    >
      {children}
    </Box>
  );
};

// ── Classification ───────────────────────────────────────────────────────────
// The three levels as a readable hierarchy with their confidences. A level the
// document omits is left out entirely rather than shown as "Unknown": saying
// less is not the same as saying you don't know.
//
// `classification.types[]` is ADDITIVE, not a single value — the canonical spec
// is explicit that a device can carry more than one role at once (its own worked
// example: a host that is both a domain controller AND a DNS server). The
// breadcrumb's third pill is only the highest-confidence one; every other
// candidate renders below as "Also matches", each with its own confidence and
// source (`source` lives per-candidate now — there is no flat
// `classification.source[]` to fall back to any more, verified: no rule in the
// whole ruleset writes one).
const ClassificationSection = ({ klass }) => {
  if (!klass.assetType && klass.levels.length === 0) return null;
  const DeploymentIcon = klass.deploymentIcon;
  const otherTypes = klass.typeCandidates?.slice(1) ?? [];
  return (
    <SidePanelSection title="Asset Classification">
      <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 0.8 }}>
        {klass.levels.map((level, i) => (
          <Box key={level.key} sx={{ display: "flex", alignItems: "center", gap: 0.6 }}>
            {i > 0 && <ArrowForwardIcon sx={{ color: colors.grey[600], fontSize: 15 }} />}
            <Tooltip title={[
              level.key.replace(/_/g, " "),
              `confidence ${level.confidence ?? "—"}`,
              level.source ? `source ${level.source}` : null,
            ].filter(Boolean).join(" · ")}>
              <Chip label={humanizeTaxonomy(level.value)} size="small" sx={{ height: 27, px: 0.3, bgcolor: i === klass.levels.length - 1 ? "rgba(69,144,149,0.2)" : "#ffffff0b", border: i === klass.levels.length - 1 ? "1px solid rgba(69,144,149,0.35)" : "1px solid rgba(255,255,255,0.08)", color: i === klass.levels.length - 1 ? colors.grey[100] : colors.grey[300], fontWeight: i === klass.levels.length - 1 ? 700 : 500 }} />
            </Tooltip>
          </Box>
        ))}
      </Box>

      {klass.levels.length < 3 && (
        <Typography sx={{ fontSize: 11, color: colors.grey[500], mt: 1 }}>
          Classification is available up to {humanizeTaxonomy(klass.levels[klass.levels.length - 1]?.value)}.
        </Typography>
      )}

      {klass.deployment && (
        <SidePanelKV
          label="Deployment"
          value={humanizeTaxonomy(klass.deployment)}
          icon={DeploymentIcon ? <DeploymentIcon style={{ fontSize: 17 }} /> : null}
        />
      )}

      {otherTypes.length > 0 && (
        <Box sx={{ mt: 1.5, pt: 1, borderTop: "1px solid rgba(255,255,255,0.06)" }}>
          <Typography sx={{ fontSize: 10.5, color: colors.grey[500], textTransform: "uppercase", letterSpacing: 0.5, mb: 0.6 }}>
            Also matches
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
            {otherTypes.map((t, i) => (
              <Tooltip key={i} title={[`confidence ${t.confidence ?? "—"}`, t.source ? `source ${t.source}` : null].filter(Boolean).join(" · ")}>
                <Chip
                  label={humanizeTaxonomy(t.value)}
                  size="small"
                  sx={{ bgcolor: "#ffffff0d", color: colors.grey[300], fontSize: 11, height: 22, fontWeight: 500 }}
                />
              </Tooltip>
            ))}
          </Box>
        </Box>
      )}
    </SidePanelSection>
  );
};

// ── Operating system ─────────────────────────────────────────────────────────
// Four independent levels, each with its own confidence and source. Labelled
// Family / Distribution / Release / Build rather than by field name, because
// `os.version` holds the commercial release name (or, for Linux, the bare
// release number once `os.distribution` carries the distro name) and `os.build`
// the build number — the field names invite exactly the swap this labelling
// prevents. `OS_LEVELS` drives which rows render, so a row simply doesn't
// appear when that level (e.g. distribution, on a non-Linux asset) is absent.
const OsSection = ({ os }) => {
  if (!os) return null;
  const present = OS_LEVELS.filter((l) => os[l.key]);
  if (present.length === 0) return null;
  const Icon = osIcon(os);

  return (
    <SidePanelSection title="Operating System">
      {present.map((level, i) => (
        <SidePanelKV
          key={level.key}
          label={level.label}
          icon={i === 0 && Icon ? <Icon style={{ fontSize: 18 }} /> : null}
          value={os[level.key]}
          mono={level.key === "build"}
          action={
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <ConfidenceChip confidence={os[`${level.key}_confidence`]} />
              <SourceChip source={os[`${level.key}_source`]} />
            </Box>
          }
        />
      ))}
    </SidePanelSection>
  );
};

// ── Risk score with its explanation ──────────────────────────────────────────
// Two scores live on the document now. `dynamicScore` (dynamic_risk_score) is
// recomputed on a schedule from live telemetry — the same declarative signals
// `staticScore` (risk_score) uses (zone risk, tags, EOL OS, Purdue proximity...)
// plus event-driven ones only it sees (`security_events_<tier>(matched/total)`,
// `ot_security_events_<tier>(matched/total)`). It's the more current read on
// "how worried should I be right now", so it leads the card; the static score
// stays visible underneath as the slower-moving baseline, not gone. Documents
// predating dynamic_risk_score (dynamicScore null) fall back to the static
// score as primary and nothing regresses.
//
// The factor list is `dynamicFactors` (a superset of `staticFactors`), each
// tagged via mergeRiskFactors() as shared with the static score or "dynamic only" —
// the explanation for why the two scores can disagree, not just that they do.
const RiskScoreCard = ({ dynamicScore, dynamicFactors, staticScore, staticFactors }) => {
  const hasDynamic = typeof dynamicScore === "number";
  const primaryScore = hasDynamic ? dynamicScore : staticScore;
  const style = riskScoreStyle(primaryScore);
  const showStaticBadge = hasDynamic && typeof staticScore === "number";
  const staticStyle = riskScoreStyle(staticScore);
  const merged = useMemo(() => mergeRiskFactors(staticFactors, dynamicFactors), [staticFactors, dynamicFactors]);
  // No dynamic_risk_factors at all (predates the field) — fall back to the
  // static breakdown instead of showing an empty list.
  const parsed = useMemo(
    () => (merged.length > 0 ? merged : parseRiskFactors(staticFactors).map((f) => ({ ...f, shared: true }))),
    [merged, staticFactors]
  );
  const weighted = parsed.filter((f) => f.weight != null);
  const summed = weighted.reduce((sum, f) => sum + f.weight, 0);
  const maxWeight = weighted.reduce((max, f) => Math.max(max, f.weight), 0);

  if (typeof primaryScore !== "number" && parsed.length === 0) return null;

  return (
    <SidePanelSection title="Risk Score">
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1 }}>
        <Box
          sx={{
            width: 66, height: 66, borderRadius: "50%", flexShrink: 0,
            display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
            border: `4px solid ${style.color}`, color: style.color,
          }}
        >
          <Typography sx={{ fontSize: 22, fontWeight: 800, lineHeight: 1 }}>
            {typeof primaryScore === "number" ? primaryScore : "—"}
          </Typography>
          <Typography sx={{ fontSize: 8, opacity: 0.7 }}>/ 100</Typography>
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.8, flexWrap: "wrap" }}>
            <Chip label={style.label} size="small" sx={{ bgcolor: style.bg, color: style.color, fontWeight: 700, fontSize: 10.5, height: 20 }} />
            {hasDynamic && (
              <Tooltip title="Recomputed periodically from network/security-event telemetry, on top of the same signals the baseline uses — not real-time">
                <Chip label="Dynamic" size="small" sx={{ bgcolor: "#45909522", color: "#459095", fontWeight: 700, fontSize: 9.5, height: 18 }} />
              </Tooltip>
            )}
          </Box>
          <Typography sx={{ fontSize: 11.5, color: colors.grey[400], mt: 0.6 }}>
            {parsed.length > 0
              ? `${parsed.length} contributing factor${parsed.length > 1 ? "s" : ""}`
              : "No contributing factors recorded."}
          </Typography>
          {/* Worth surfacing rather than hiding: if the parts don't add up to the
              whole, the score was capped or includes an unlisted term. */}
          {weighted.length > 0 && typeof primaryScore === "number" && summed !== primaryScore && (
            <Typography sx={{ fontSize: 10.5, color: colors.grey[600], mt: 0.2 }}>
              Listed factors sum to {summed} — the score is capped or includes an unlisted term.
            </Typography>
          )}
        </Box>
      </Box>

      {/* Between the live score's own counter and the factor list, not fighting
          the primary circle for attention — a chip, not a caption, so it still
          reads at a glance. */}
      {showStaticBadge && (
        <Tooltip title="Baseline risk score (risk_score) — the same declarative signals as the live score, without event telemetry">
          <Chip
            label={`Baseline ${staticScore}/100`}
            size="small"
            sx={{ bgcolor: staticStyle.bg, color: staticStyle.color, fontWeight: 700, fontSize: 10.5, height: 20, mb: 1.2 }}
          />
        </Tooltip>
      )}

      {parsed.length > 0 && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
          {parsed.map((f, i) => (
            <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography sx={{ fontSize: 11.5, color: colors.grey[200], flex: "1 1 auto", minWidth: 0 }}>
                {f.label}
              </Typography>
              {hasDynamic && !f.shared && (
                <Tooltip title="Only in the dynamic score — the baseline doesn't see this signal">
                  <Chip label="dynamic only" size="small" sx={{ bgcolor: "#45909522", color: "#459095", fontWeight: 700, fontSize: 9, height: 16, flexShrink: 0 }} />
                </Tooltip>
              )}
              <Box sx={{ width: 90, height: 5, borderRadius: 3, backgroundColor: "#161616", overflow: "hidden", flexShrink: 0 }}>
                <Box
                  sx={{
                    height: "100%",
                    width: maxWeight > 0 && f.weight != null ? `${Math.round((f.weight / maxWeight) * 100)}%` : 0,
                    backgroundColor: style.color,
                    borderRadius: 3,
                  }}
                />
              </Box>
              <Typography sx={{ fontSize: 11.5, fontFamily: "monospace", fontWeight: 700, color: style.color, minWidth: 34, textAlign: "right", flexShrink: 0 }}>
                {f.weight != null ? `+${f.weight}` : "—"}
              </Typography>
            </Box>
          ))}
        </Box>
      )}
    </SidePanelSection>
  );
};

// ── Exposed services ─────────────────────────────────────────────────────────
// `services[]` holds only server-side exposed ports now, so there is nothing to
// filter by type. The tags are the point: they are what turns a port list into a
// readable attack surface, so they are badges you can filter and sort by rather
// than an afterthought.
const SORTS = [
  { key: "port",      label: "Port" },
  { key: "tags",      label: "Most tags" },
  { key: "last_seen", label: "Last seen" },
];

// Pinned to a fixed-width rail on the Security tab (see the two-column layout
// below). A fixed-height scroll window keeps the card's own height constant
// regardless of how many ports an asset has or which tag filter is active —
// paging used to do this by capping row count, but a last page with only one
// or two rows still shrank the card and shifted everything below it.
const SECURITY_LIST_MAX_HEIGHT = 420;

const ServicesSection = ({ services, tagColor }) => {
  const [activeTags, setActiveTags] = useState([]);
  const [sort, setSort] = useState("port");

  const allTags = useMemo(
    () => [...new Set((services || []).flatMap((s) => s.tags || []))].sort(),
    [services]
  );

  const visible = useMemo(() => {
    const filtered = (services || []).filter(
      (s) => activeTags.length === 0 || activeTags.every((t) => (s.tags || []).includes(t))
    );
    const sorted = filtered.slice();
    if (sort === "tags") sorted.sort((a, b) => (b.tags?.length ?? 0) - (a.tags?.length ?? 0));
    else if (sort === "last_seen") sorted.sort((a, b) => new Date(b.last_seen?.$date ?? b.last_seen ?? 0) - new Date(a.last_seen?.$date ?? a.last_seen ?? 0));
    else sorted.sort((a, b) => (a.port ?? 0) - (b.port ?? 0));
    return sorted;
  }, [services, activeTags, sort]);

  if ((services || []).length === 0) return null;

  const toggleTag = (t) =>
    setActiveTags((cur) => (cur.includes(t) ? cur.filter((x) => x !== t) : [...cur, t]));

  return (
    <SidePanelSection title={`Exposed Services (${visible.length}${visible.length !== services.length ? ` of ${services.length}` : ""})`}>
      {/* Fixed-height column, not just a maxHeight on the list below: a filter
          row here and none on Vulnerabilities would otherwise make this card
          taller even with both lists capped at the same maxHeight — height is
          reserved up front so the two cards always match, whatever's inside. */}
      <Box sx={{ display: "flex", flexDirection: "column", height: SECURITY_LIST_MAX_HEIGHT }}>
        {(allTags.length > 0 || services.length > 1) && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexWrap: "wrap", mb: 1, flexShrink: 0 }}>
            {allTags.map((t) => {
              const c = tagColor(t);
              const active = activeTags.includes(t);
              return (
                <Chip
                  key={t}
                  label={t}
                  size="small"
                  onClick={() => toggleTag(t)}
                  sx={{
                    bgcolor: active ? c.color : c.bg,
                    color: active ? "#111" : c.color,
                    fontSize: 10, height: 19, fontWeight: 600, cursor: "pointer",
                    border: `1px solid ${active ? c.color : "transparent"}`,
                  }}
                />
              );
            })}
            <Box sx={{ ml: "auto", display: "flex", gap: 0.3 }}>
              {SORTS.map((s) => (
                <Chip
                  key={s.key}
                  label={s.label}
                  size="small"
                  onClick={() => setSort(s.key)}
                  sx={{
                    bgcolor: sort === s.key ? "#45909533" : "transparent",
                    color: sort === s.key ? "#7de8ed" : colors.grey[500],
                    fontSize: 9.5, height: 18, cursor: "pointer",
                  }}
                />
              ))}
            </Box>
          </Box>
        )}

        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.6, flex: 1, overflowY: "auto", pr: 0.5 }}>
          {visible.map((s, i) => {
            const fs = parseDate(s.first_seen);
            const ls = parseDate(s.last_seen);
            const proto = s.protocol ? `/${s.protocol.toLowerCase()}` : "";
            return (
              <Box key={i} sx={{ borderRadius: 1, backgroundColor: "#222", border: `1px solid ${colors.grey[800]}`, p: 1 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                  <Typography sx={{ color: "#459095", fontFamily: "monospace", fontWeight: 600, fontSize: 13 }}>
                    {s.port}{proto}
                  </Typography>
                  <Typography sx={{ color: colors.grey[200], fontSize: 12.5 }}>{s.name}</Typography>
                  {(s.tags || []).map((tag, ti) => {
                    const c = tagColor(tag);
                    return (
                      <Chip
                        key={ti}
                        label={tag}
                        size="small"
                        sx={{ bgcolor: c.bg, color: c.color, fontSize: 10, height: 17, fontWeight: 600 }}
                      />
                    );
                  })}
                </Box>
                {(fs || ls) && (
                  <Typography sx={{ color: colors.grey[500], fontSize: 11, mt: 0.5 }}>
                    first seen {fs ? fs.format("YYYY-MM-DD HH:mm") : "—"}
                    {ls ? ` · last seen ${ls.format("YYYY-MM-DD HH:mm")}` : ""}
                  </Typography>
                )}
              </Box>
            );
          })}
          {visible.length === 0 && <Empty>No service carries all the selected tags.</Empty>}
        </Box>
      </Box>
    </SidePanelSection>
  );
};

// ── Operations ───────────────────────────────────────────────────────────────
// `operations[]` is the OT/antlia catalog of what this asset has done or had
// done to it — same client/server-role split as services, plus a read/write
// flag per operation rather than a tag list.
const OPERATIONS_SORTS = [
  { key: "operation",  label: "Operation" },
  { key: "last_seen",  label: "Last seen" },
];

// client vs server get their own color, independent of the read/write chip,
// so at a glance which side of the operation this asset was on is a color,
// not a word you have to read.
const OPERATION_ROLE_COLORS = {
  client: { bg: "#7b8bff22", color: "#7b8bff" },
  server: { bg: "#ffca2822", color: "#ffca28" },
};
const operationRoleColor = (role) => OPERATION_ROLE_COLORS[role] ?? { bg: "#45909533", color: "#7de8ed" };

const OperationsSection = ({ operations }) => {
  const [roleFilter, setRoleFilter] = useState(null);
  const [sort, setSort] = useState("operation");

  const visible = useMemo(() => {
    const filtered = (operations || []).filter(
      (o) => !roleFilter || o.role === roleFilter
    );
    const sorted = filtered.slice();
    if (sort === "last_seen") sorted.sort((a, b) => new Date(b.last_seen?.$date ?? b.last_seen ?? 0) - new Date(a.last_seen?.$date ?? a.last_seen ?? 0));
    else sorted.sort((a, b) => (a.operation ?? "").localeCompare(b.operation ?? ""));
    return sorted;
  }, [operations, roleFilter, sort]);

  // Grouped by protocol — an asset speaking several OT protocols (Modbus as a
  // server, S7 as a client, ...) reads as one undifferentiated list otherwise;
  // groups themselves sorted alphabetically, the chosen sort applies within
  // each group. Protocol is the group header now, so it drops off each row.
  const grouped = useMemo(() => {
    const byProtocol = new Map();
    visible.forEach((o) => {
      const p = o.protocol || "unknown";
      if (!byProtocol.has(p)) byProtocol.set(p, []);
      byProtocol.get(p).push(o);
    });
    return [...byProtocol.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [visible]);

  if ((operations || []).length === 0) {
    return (
      <SidePanelSection title="Operations">
        <Empty>No OT/Antlia operations recorded for this asset.</Empty>
      </SidePanelSection>
    );
  }

  const roles = [...new Set((operations || []).map((o) => o.role).filter(Boolean))];

  return (
    <SidePanelSection title={`Operations (${visible.length}${visible.length !== operations.length ? ` of ${operations.length}` : ""})`}>
      {(roles.length > 1 || operations.length > 1) && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexWrap: "wrap", mb: 1 }}>
          {roles.map((r) => {
            const c = operationRoleColor(r);
            const active = roleFilter === r;
            return (
              <Chip
                key={r}
                label={r}
                size="small"
                onClick={() => setRoleFilter((cur) => (cur === r ? null : r))}
                sx={{
                  bgcolor: active ? c.color : c.bg,
                  color: active ? "#111" : c.color,
                  fontSize: 10, height: 19, fontWeight: 600, cursor: "pointer",
                  border: `1px solid ${active ? c.color : "transparent"}`,
                }}
              />
            );
          })}
          <Box sx={{ ml: "auto", display: "flex", gap: 0.3 }}>
            {OPERATIONS_SORTS.map((s) => (
              <Chip
                key={s.key}
                label={s.label}
                size="small"
                onClick={() => setSort(s.key)}
                sx={{
                  bgcolor: sort === s.key ? "#45909533" : "transparent",
                  color: sort === s.key ? "#7de8ed" : colors.grey[500],
                  fontSize: 9.5, height: 18, cursor: "pointer",
                }}
              />
            ))}
          </Box>
        </Box>
      )}

      <Box sx={{ maxHeight: SECURITY_LIST_MAX_HEIGHT, overflowY: "auto", pr: 0.5 }}>
        {grouped.map(([protocol, ops]) => (
          <Box key={protocol} sx={{ mb: 1.2, "&:last-of-type": { mb: 0 } }}>
            <GroupLabel>{protocol}</GroupLabel>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.6 }}>
              {ops.map((o, i) => {
                const fs = parseDate(o.first_seen);
                const ls = parseDate(o.last_seen);
                return (
                  <Box key={i} sx={{ borderRadius: 1, backgroundColor: "#222", border: `1px solid ${colors.grey[800]}`, p: 1 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                      <Typography sx={{ color: colors.grey[200], fontSize: 12.5 }}>{o.operation}</Typography>
                      {o.role && (
                        <Chip
                          label={o.role}
                          size="small"
                          sx={{ bgcolor: operationRoleColor(o.role).bg, color: operationRoleColor(o.role).color, fontSize: 10, height: 17, fontWeight: 600 }}
                        />
                      )}
                      <Chip
                        label={o.is_write ? "write" : "read"}
                        size="small"
                        sx={{
                          bgcolor: o.is_write ? "#e8845933" : "#4cceac33",
                          color: o.is_write ? "#e88459" : "#4cceac",
                          fontSize: 10, height: 17, fontWeight: 600,
                        }}
                      />
                    </Box>
                    {(fs || ls) && (
                      <Typography sx={{ color: colors.grey[500], fontSize: 11, mt: 0.5 }}>
                        first seen {fs ? fs.format("YYYY-MM-DD HH:mm") : "—"}
                        {ls ? ` · last seen ${ls.format("YYYY-MM-DD HH:mm")}` : ""}
                      </Typography>
                    )}
                  </Box>
                );
              })}
            </Box>
          </Box>
        ))}
        {visible.length === 0 && <Empty>No operation matches the selected role.</Empty>}
      </Box>
    </SidePanelSection>
  );
};

// ── Software ─────────────────────────────────────────────────────────────────
// One row per entry with name and version in their own columns. The names are
// often protocol identifiers (`SSDP-SERVER`) rather than product names: this is
// text the device put on the wire, not an inventory of installed software, and
// saying so keeps the section from being read as more than it is.
//
// Two failure modes this guards against: (1) a version string with no spaces
// (a long User-Agent or firmware blob) — an unequal "1fr auto" grid lets that
// single "word" dictate both columns' width, squeezing the name column down to
// nothing and forcing it to wrap one character per line; equal minmax(0,1fr)
// columns plus TruncatedText keep both sides bounded instead. (2) an asset with
// many banners (HTTP alone can produce a dozen) turning the tab into one long
// scroll — capped to a handful by default, with an explicit toggle for the rest.
const SOFTWARE_VISIBLE_LIMIT = 8;

const SoftwareSection = ({ software }) => {
  const [showAll, setShowAll] = useState(false);
  if (!software?.length) return null;
  const visible = showAll ? software : software.slice(0, SOFTWARE_VISIBLE_LIMIT);
  const hidden = software.length - visible.length;

  return (
    <SidePanelSection title={`Software Banners (${software.length})`}>
      <Typography sx={{ fontSize: 11, color: colors.grey[600], fontStyle: "italic", mb: 0.8 }}>
        What the device announced on the wire — not an inventory of installed software.
        Names may be protocol identifiers rather than products.
      </Typography>
      <Box sx={{ display: "flex", flexDirection: "column" }}>
        {visible.map((sw, i) => (
          <Box
            key={i}
            sx={{
              display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)", gap: 1.2, py: 0.55,
              borderBottom: `1px dashed ${colors.grey[800]}`,
              "&:last-of-type": { borderBottom: "none" },
            }}
          >
            <Box sx={{ fontSize: 12.5, color: colors.grey[100] }}>
              <TruncatedText>{sw.name || "—"}</TruncatedText>
            </Box>
            <Box sx={{ fontSize: 12, color: colors.grey[400] }}>
              <TruncatedText mono align="right">{sw.version || "—"}</TruncatedText>
            </Box>
          </Box>
        ))}
      </Box>
      {hidden > 0 && (
        <Button
          size="small"
          onClick={() => setShowAll(true)}
          sx={{ mt: 0.8, fontSize: 11, textTransform: "none", color: "#459095" }}
        >
          Show {hidden} more banner{hidden > 1 ? "s" : ""}
        </Button>
      )}
    </SidePanelSection>
  );
};

// ── Notes ─────────────────────────────────────────────────────────────────────
// Edited inline, right on the card — no separate dialog. Notes used to be one
// of the fields EditAssetDialog wrote through updateAssetFields; that endpoint
// is unchanged, only the entry point moved next to the text it edits.
// Module-scope (not defined inside AssetDetailContent): a component declared
// inside another component's render body is a new function identity every
// render, which remounts the subtree on every keystroke — see
// EditAssetDialog.jsx's OverrideSelect/ClassificationSelect for the same fix.
const NotesCard = ({ notes, onSave }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(notes ?? "");
  const [saving, setSaving] = useState(false);

  const startEdit = () => { setDraft(notes ?? ""); setEditing(true); };
  const cancel = () => setEditing(false);
  const save = async () => {
    setSaving(true);
    try {
      await onSave(draft.trim());
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  if (!onSave) {
    return notes ? (
      <Box sx={{ mb: 2.5, borderRadius: 1.5, backgroundColor: "#1c1c1c", border: `1px solid ${colors.grey[800]}`, p: 1.5 }}>
        <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#459095", textTransform: "uppercase", letterSpacing: 0.8, mb: 0.8 }}>Notes</Typography>
        <Typography sx={{ fontSize: 12.5, color: colors.grey[300], whiteSpace: "pre-wrap" }}>{notes}</Typography>
      </Box>
    ) : null;
  }

  if (editing) {
    return (
      <Box sx={{ mb: 2.5, borderRadius: 1.5, backgroundColor: "#1c1c1c", border: "1px solid #459095", p: 1.5 }}>
        <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#459095", textTransform: "uppercase", letterSpacing: 0.8, mb: 0.8 }}>Notes</Typography>
        <TextField
          autoFocus
          fullWidth
          multiline
          minRows={2}
          size="small"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          disabled={saving}
        />
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 0.6, mt: 0.8 }}>
          <IconButton size="small" onClick={cancel} disabled={saving} sx={{ color: colors.grey[400] }}>
            <CloseIcon sx={{ fontSize: 16 }} />
          </IconButton>
          <IconButton size="small" onClick={save} disabled={saving} sx={{ color: "#4cceac" }}>
            <CheckIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Box>
      </Box>
    );
  }

  return notes ? (
    <Box sx={{ mb: 2.5, borderRadius: 1.5, backgroundColor: "#1c1c1c", border: `1px solid ${colors.grey[800]}`, p: 1.5 }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.8 }}>
        <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#459095", textTransform: "uppercase", letterSpacing: 0.8 }}>Notes</Typography>
        <Tooltip title="Edit notes">
          <IconButton size="small" onClick={startEdit} sx={{ color: colors.grey[400], p: 0.3 }}>
            <EditIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Tooltip>
      </Box>
      <Typography sx={{ fontSize: 12.5, color: colors.grey[300], whiteSpace: "pre-wrap" }}>{notes}</Typography>
    </Box>
  ) : (
    <Box
      onClick={startEdit}
      sx={{
        mb: 2.5, borderRadius: 1.5, backgroundColor: "#1c1c1c", border: `1px dashed ${colors.grey[700]}`, p: 1.5,
        cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "space-between",
        "&:hover": { borderColor: "#459095" },
      }}
    >
      <Typography sx={{ fontSize: 12, color: colors.grey[400] }}>No notes yet</Typography>
      <Typography sx={{ fontSize: 12, color: "#459095", fontWeight: 600 }}>+ Add Note</Typography>
    </Box>
  );
};

// Full asset detail body — shared by the quick-glance drawer (AssetSidePanel) and the
// full Asset Detail page. `assetId`/`ackMap`/`onAcknowledge`/`onReopen` are optional:
// pass all four to enable CVE acknowledgment on every VulnerabilityList in this view
// (omit them and it behaves exactly like the pre-ack-feature drawer did).
//
// `section` is the point of this component: identity and security are two separate
// tabs, never one page. "Identity" answers *what is this device* (classification,
// OS, every protocol's declared identity, network, names, software banners);
// "Security" answers *how worried should I be* (risk score and its explanation,
// CVEs, exposed surface) — and is where future security-specific signals land,
// not Identity. OT protocol read/write activity has its own "ot-operations"
// section/tab instead — a record of what happened, not a risk signal. The
// caller owns the tab chrome —
// the full page and the drawer each draw their own — and passes the section to
// show. Mixing the two planes into one flat key/value list, which is what this
// replaced, makes neither question answerable.
//
// `layout="columns"` only affects how the blocks of the *selected* section are
// arranged on a wide page; it never puts identity and security side by side.
const AssetDetailContent = ({
  row,
  tagColor = staticTagColor,
  onShowIpHistory,
  assetId,
  ackMap,
  onAcknowledge,
  onReopen,
  exposureByIp,
  layout = "single",
  section = "identity",
  orionVisible = true,
  onSaveNotes,
}) => {
  const [showFingerprint, setShowFingerprint] = useState(false);

  const klass = useMemo(() => resolveClassification(row.classification), [row.classification]);
  const assetVulns = allVulnerabilitiesForAsset(row);
  const worstAssetVuln = worstVulnerability(assetVulns);
  const alternatives = hostnameAlternatives(row.host_names, row.hostname_source);

  const ackProps = { assetId, ackMap, onAcknowledge, onReopen };

  // ── Network ─────────────────────────────────────────────────────────────
  const networkSection = (
    <SidePanelSection title="Network">
      <SidePanelKV
        label="IP"
        value={row.ip}
        mono
        copyable
        action={row.ip_history?.length > 0 && (
          <Tooltip title={`Address changes (${row.ip_history.length})`}>
            <Link
              component="button"
              type="button"
              onClick={() => onShowIpHistory?.(row)}
              sx={{ color: "#7b8bff", fontSize: 11, fontWeight: 600 }}
            >
              IP History
            </Link>
          </Tooltip>
        )}
      />
      <SidePanelKV label="IPv6" value={row.ip_v6} mono copyable />
      <SidePanelKV label="MAC" value={row.mac} mono copyable />
      {row.alt_macs?.length > 0 && <SidePanelKV label="Alt MACs" value={row.alt_macs.join(", ")} mono />}
      <SidePanelKV
        label="Vendor"
        value={row.vendor}
        action={row.vendor_source && (
          <Tooltip
            title={
              row.vendor_source === "oui"
                ? "Inferred from the MAC address prefix"
                : row.vendor_source === "device-identity"
                  ? "Declared by the device itself — more reliable than the OUI lookup"
                  : row.vendor_source
            }
          >
            <span><SourceChip source={row.vendor_source} /></span>
          </Tooltip>
        )}
      />
      <SidePanelKV label="VLAN" value={row.vlan} />
      <SidePanelKV label="Inner VLAN" value={row.inner_vlan} />
      <SidePanelKV label="First Seen" value={fmt(row.first_seen)} mono />
      <SidePanelKV label="Last Seen" value={fmt(row.last_seen)} mono />

      {/* Other Names lives inside this same card, not as its own flow block —
          the masonry layout below packs cards by rendered height, which
          routinely put "Other Names" in a different column than "Network"
          despite sitting right after it in block order. Nesting it here is
          the only way to guarantee it always reads directly under Network. */}
      {alternatives.length > 0 && (
        <Box sx={{ mt: 1.5, pt: 1, borderTop: "1px solid rgba(255,255,255,0.06)" }}>
          <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: colors.grey[500], textTransform: "uppercase", letterSpacing: 0.5, mb: 0.6 }}>
            Other Names
          </Typography>
          <Typography sx={{ fontSize: 11, color: colors.grey[600], fontStyle: "italic", mb: 0.8 }}>
            Every name this asset answers to. The one in the header won because its source
            is the most self-declared of the set.
          </Typography>
          {alternatives.map((a) => (
            <SidePanelKV key={a.source} label={a.label} value={a.name} mono />
          ))}
        </Box>
      )}
    </SidePanelSection>
  );

  // ── Device-declared identity ─────────────────────────────────────────────
  // Guarded on actual field values, not just `row.device` being an object: a
  // present-but-empty device{} would otherwise render a title-only box with
  // nothing under it (every SidePanelKV inside returns null on an empty value).
  const hasDeviceInfo = row.device && [
    row.device.vendor, row.device.model, row.device.firmware, row.device.serial,
    row.device.serial_secondary, row.device.station_name, row.device.modality,
    row.device.app_id, row.device.facility_name,
  ].some((v) => v != null && v !== "");
  const deviceSection = hasDeviceInfo && (
    <SidePanelSection title="Declared by the Device">
      <Typography sx={{ fontSize: 11, color: colors.grey[600], fontStyle: "italic", mb: 0.8 }}>
        Identity the device states about itself — distinct from the OUI-derived vendor
        above, and more reliable.
      </Typography>
      <SidePanelKV label="Vendor" value={row.device.vendor} />
      <SidePanelKV label="Model" value={row.device.model} mono />
      <SidePanelKV label="Firmware" value={row.device.firmware} mono />
      <SidePanelKV label="Serial" value={row.device.serial} mono />
      <SidePanelKV label="Serial (secondary)" value={row.device.serial_secondary} mono />
      <SidePanelKV label="Station Name" value={row.device.station_name} />
      <SidePanelKV label="Modality" value={row.device.modality} />
      <SidePanelKV label="App ID" value={row.device.app_id} mono />
      <SidePanelKV label="Facility Name" value={row.device.facility_name} />
      <SidePanelKV label="Declared via" value={row.device.source} action={<SourceChip source={row.device.source} />} />
    </SidePanelSection>
  );

  // SNMP/DHCP no longer get their own dedicated section here — both are
  // ordinary protocol blocks now, shown like every other protocol in
  // ProtocolIdentitySection (see DUPLICATED_ELSEWHERE there).

  // ── Fingerprint — technical material, collapsed by default ───────────────
  const hasFingerprint = row.fingerprint && (row.fingerprint.tcp || row.fingerprint.ja4 || row.fingerprint.http);
  const fingerprintSection = hasFingerprint && (
    <SidePanelSection
      title="Fingerprint"
      action={
        <Button
          size="small"
          endIcon={<ExpandMoreIcon sx={{ fontSize: "16px !important", transform: showFingerprint ? "rotate(180deg)" : "none", transition: "transform 150ms" }} />}
          onClick={() => setShowFingerprint((v) => !v)}
          sx={{ fontSize: 11, textTransform: "none", color: colors.grey[400] }}
        >
          {showFingerprint ? "Hide" : "Show"}
        </Button>
      }
    >
      {!showFingerprint ? (
        <Typography sx={{ fontSize: 11.5, color: colors.grey[600], fontStyle: "italic" }}>
          Low-level TCP / TLS / HTTP characteristics — the raw evidence behind several
          of the inferences above.
        </Typography>
      ) : (
        <>
          {row.fingerprint.tcp && (
            <>
              <GroupLabel>TCP</GroupLabel>
              <SidePanelKV label="Observed TTL" value={row.fingerprint.tcp.observed_ttl} mono />
              <SidePanelKV label="Est. Initial TTL" value={row.fingerprint.tcp.estimated_initial_ttl} mono />
              <SidePanelKV label="Window Size" value={row.fingerprint.tcp.window_size} mono />
              <SidePanelKV label="Don't Fragment" value={row.fingerprint.tcp.df != null ? String(row.fingerprint.tcp.df) : null} />
              <SidePanelKV label="MSS" value={row.fingerprint.tcp.mss} mono />
              <SidePanelKV label="Window Scale" value={row.fingerprint.tcp.window_scale} mono />
              <SidePanelKV label="SACK OK" value={row.fingerprint.tcp.sack_ok != null ? String(row.fingerprint.tcp.sack_ok) : null} />
              <SidePanelKV label="Timestamp Present" value={row.fingerprint.tcp.ts_present != null ? String(row.fingerprint.tcp.ts_present) : null} />
              <SidePanelKV label="Quality" value={row.fingerprint.tcp.quality} />
              <SidePanelKV label="Est. MTU" value={row.fingerprint.tcp.estimated_mtu} mono />
              <SidePanelKV label="Link Type" value={row.fingerprint.tcp.link_type_guess} />
              {/* -1 is the probe's "could not estimate" marker, not an uptime */}
              <SidePanelKV
                label="Est. Uptime (s)"
                value={row.fingerprint.tcp.estimated_uptime_sec > 0 ? row.fingerprint.tcp.estimated_uptime_sec : null}
                mono
              />
            </>
          )}
          {row.fingerprint.ja4 && (
            <>
              <GroupLabel>JA4</GroupLabel>
              <SidePanelKV label="Value" value={row.fingerprint.ja4.value} mono copyable />
              <SidePanelKV label="Quality" value={row.fingerprint.ja4.quality} />
            </>
          )}
          {row.fingerprint.http && (
            <>
              <GroupLabel>HTTP</GroupLabel>
              <SidePanelKV
                label="Header Order"
                value={Array.isArray(row.fingerprint.http.header_order) ? row.fingerprint.http.header_order.join(", ") : row.fingerprint.http.header_order}
                mono
              />
              <SidePanelKV label="Accept-Language" value={row.fingerprint.http.accept_language} />
            </>
          )}
        </>
      )}
    </SidePanelSection>
  );

  // ── Notes ───────────────────────────────────────────────────────────────
  const notesBlock = <NotesCard notes={row.notes} onSave={onSaveNotes} />;

  // ── Security blocks — "how worried should I be" ─────────────────────────
  // Plain `null` when there are no vulnerabilities — not a Fragment wrapping a
  // falsy condition, which is itself a truthy React element and would still
  // pass the `blocks.filter((b) => b.node)` check below, leaving an empty
  // bordered box on the page.
  const vulnOverviewBlock = assetVulns.length > 0 ? (
    <SidePanelStatGrid
      title="Vulnerability Overview"
      tiles={[
        { label: "Total CVEs", value: assetVulns.length },
        { label: "Critical",   value: assetVulns.filter((v) => vulnSeverityTier(v) === "critical").length, color: severityStyleForTier("critical").color },
        { label: "High",       value: assetVulns.filter((v) => vulnSeverityTier(v) === "high").length, color: severityStyleForTier("high").color },
        { label: "Medium",     value: assetVulns.filter((v) => vulnSeverityTier(v) === "medium").length, color: severityStyleForTier("medium").color },
        { label: "Low",        value: assetVulns.filter((v) => vulnSeverityTier(v) === "low").length, color: severityStyleForTier("low").color },
      ]}
    >
      {worstAssetVuln && (
        <Typography sx={{ fontSize: 11, color: colors.grey[400] }}>
          Worst: <b style={{ color: severityStyleForTier(vulnSeverityTier(worstAssetVuln)).color }}>{worstAssetVuln.cve_id || "Unknown CVE"}</b>
          {worstAssetVuln.matched_on ? ` — matched on ${worstAssetVuln.matched_on}` : ""}
        </Typography>
      )}
    </SidePanelStatGrid>
  ) : null;

  const vulnListBlock = (
    <SidePanelSection
      title={`Vulnerabilities${assetVulns.length ? ` (${assetVulns.length})` : ""}`}
      action={assetVulns.length > 0 && <ExposureChip row={row} exposureByIp={exposureByIp} />}
    >
      <VulnerabilityList vulns={assetVulns} emptyMessage="No known vulnerabilities." maxHeight={SECURITY_LIST_MAX_HEIGHT} {...ackProps} />
    </SidePanelSection>
  );

  // Each tab is a flat, ordered list of blocks. Nothing from one plane leaks into
  // the other: the identity tab never mentions risk, the security tab never
  // re-states identity fields.
  //
  // `software` and per-protocol device identity moved here from Security: both
  // describe what the device IS (banners it announced, vendor/model/serial it
  // declared over Modbus/S7/BACnet/DICOM/ONVIF/SNMP-ENTITY-MIB/...), not how
  // worried to be about it — the old flat `device{}` block covered only one
  // Antlia-sourced protocol at a time and is gone from the wire; the live
  // equivalent is `protocols{}`, which can carry several protocols' identity
  // claims on the same asset at once (see ProtocolIdentitySection.jsx). Security
  // keeps risk, vulnerabilities and exposed surface — the things this tab still
  // has room to grow (future security-specific signals
  // land here, not in Identity).
  //
  // Identity's own order is a priority ranking, not an alphabetical or "how the
  // document is shaped" one: network/names first (the one thing every asset has
  // and the fastest way to recognise it), then OS, then per-protocol identity
  // (SNMP/DHCP travel with it — same "what a protocol says about this device"
  // question), fingerprint last (already collapsed by default — technical
  // material, not a primary identity claim). Classification/legacy device/
  // software are supplementary and sit after, not because they're unimportant,
  // but because they were not named in that ranking.
  const blocks = section === "security"
    ? [
        { key: "risk",       node: <RiskScoreCard dynamicScore={row.dynamic_risk_score} dynamicFactors={row.dynamic_risk_factors} staticScore={row.risk_score} staticFactors={row.risk_factors} /> },
        { key: "vuln-stats", node: vulnOverviewBlock },
        { key: "vuln-list",  node: vulnListBlock },
        { key: "services",   node: <ServicesSection services={row.services} tagColor={tagColor} /> },
      ]
    : section === "ot-operations"
      // OT protocol read/write activity — its own tab (gated on the OT/Antlia
      // license, same as MIB is on Orion), not a Security sub-section: it isn't
      // a risk signal, just a record of what happened. `layout="single"` (see
      // the caller) renders this as one full-width stacked block, same as any
      // section with a single card.
      ? [
          { key: "operations", node: <OperationsSection operations={row.operations} /> },
        ]
    : orionVisible
      ? [
          { key: "classification", node: <ClassificationSection klass={klass} /> },
          { key: "os",             node: <OsSection os={row.os} /> },
          { key: "device",         node: deviceSection },
          { key: "network",        node: networkSection },
          { key: "protocols",      node: <ProtocolIdentitySection protocols={row.protocols} /> },
          { key: "fingerprint",    node: fingerprintSection },
          { key: "software",       node: <SoftwareSection software={row.software} /> },
          { key: "notes",          node: notesBlock },
        ]
      // Orion disabled: only Network survives on the Identity tab — everything else
      // (classification, OS, device identity, zone/notes context) depends on signal
      // Orion normally supplies.
      : [
          { key: "network", node: networkSection },
        ];

  const nodeByKey = Object.fromEntries(blocks.map(({ key, node }) => [key, node]));
  const contextKeys = section === "identity" && orionVisible ? ["notes"] : [];
  // Security has a fixed layout instead of the masonry flow below: a
  // height-balancing auto-flow doesn't know that the vuln overview belongs
  // right above the vuln score, not next to the vuln list — it packs by
  // rendered height, which routinely split that pair across columns. This
  // sub-column plus the vuln-list column and the services rail (further
  // down) are hand-assigned instead, so the tab reads as three columns:
  // how worried to be, the vulnerabilities themselves, what's exposed.
  const securityColumns = section === "security"
    ? { left: ["risk", "vuln-stats"], right: ["vuln-list"] }
    : null;
  // Security pins "services" to its own fixed-width rail (below) instead of
  // the two sub-columns above — it's excluded from flowKeys for that reason,
  // not because it's hidden.
  const railKeys = section === "security" ? ["services"] : [];
  // Everything except the context cards, in the priority order they were
  // authored in above (network/identity first, technical/supplementary last).
  const flowKeys = blocks.map((b) => b.key).filter((key) => !contextKeys.includes(key) && !railKeys.includes(key));

  // A hand-split two-array column assignment can't know in advance which
  // blocks AutoHideCard will end up hiding, so a fixed split routinely left
  // one column visibly shorter than the other. A native CSS multi-column flow
  // instead packs the actually-visible cards by their real rendered height —
  // `break-inside: avoid` keeps each card whole, so no card is ever split
  // across the two columns. (Identity only — Security uses securityColumns.)
  const flow = securityColumns ? (
    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: { xs: 2, md: 2.5 }, alignItems: "start" }}>
      <Box sx={{ display: "flex", flexDirection: "column", gap: { xs: 2, md: 2.5 } }}>
        {securityColumns.left.map((key) => <AutoHideCard key={key}>{nodeByKey[key]}</AutoHideCard>)}
      </Box>
      <Box sx={{ display: "flex", flexDirection: "column", gap: { xs: 2, md: 2.5 } }}>
        {securityColumns.right.map((key) => <AutoHideCard key={key}>{nodeByKey[key]}</AutoHideCard>)}
      </Box>
    </Box>
  ) : (
    <Box sx={{ columns: { xs: "1", md: "2" }, columnGap: { xs: 2, md: 2.5 } }}>
      {flowKeys.map((key) => (
        <AutoHideCard key={key} sx={{ breakInside: "avoid", WebkitColumnBreakInside: "avoid", mb: { xs: 2, md: 2.5 } }}>
          {nodeByKey[key]}
        </AutoHideCard>
      ))}
    </Box>
  );

  const body = layout === "columns" ? (
    railKeys.length > 0 ? (
      // Fixed two-column grid, not the flow above: the rail (Exposed
      // Services) always sits on the right, at a constant width, regardless
      // of how the flow column's cards pack.
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr minmax(420px, 34%)" }, gap: { xs: 2, md: 2.5 }, alignItems: "start" }}>
        {flow}
        <Box sx={{ display: "flex", flexDirection: "column", gap: { xs: 2, md: 2.5 } }}>
          {railKeys.map((key) => <AutoHideCard key={key}>{nodeByKey[key]}</AutoHideCard>)}
        </Box>
      </Box>
    ) : (
      <Box>
        {flow}
        {contextKeys.some((key) => nodeByKey[key]) && (
          <Box sx={{ mt: { xs: 0, md: 0.5 } }}>
            <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#459095", textTransform: "uppercase", letterSpacing: 0.8, mb: 1 }}>Asset Context</Typography>
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" }, gap: { xs: 2, md: 2.5 } }}>
              {contextKeys.map((key) => <AutoHideCard key={key}>{nodeByKey[key]}</AutoHideCard>)}
            </Box>
          </Box>
        )}
      </Box>
    )
  ) : (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {blocks.map(({ key, node }) => <AutoHideCard key={key}>{node}</AutoHideCard>)}
    </Box>
  );

  return (
    <>
      {body}
    </>
  );
};

export default AssetDetailContent;
